/**
 * Inside-сцены — полноразмерные 3D-сцены, в которые можно перейти из
 * основной по клику «Open 3D» в чате (в отличие от PoiArtifact — overlay
 * поверх текущей сцены).
 *
 * Структурно — обычные `SceneConfig`, проходят через стандартный пайплайн
 * `SceneCanvas` (прелоадер, reveal, FPS, dispose, transition-gate, HUD).
 * splatRegistry.ts делает фолбэк сюда, поэтому `isSplatScene(insideId)`
 * и `getSplatSceneConfig(insideId)` возвращают валидные значения.
 *
 * Навигация: пушится в `$sceneStack` с `transient: true` — URL остаётся
 * на родительской сцене (`/kaaba`), кнопка «назад» в App.tsx вытаскивает
 * через `goBack()`.
 */
import type { SceneConfig } from '../sceneConfig';
import { kaabaInsideSceneConfig } from './kaabaInside';

const insideSceneConfigs: Record<string, SceneConfig> = {
  // Backend может прислать любой из двух id — мапим оба на одну сцену.
  'kaaba-inside': kaabaInsideSceneConfig,
  'kaaba-inside-scene': kaabaInsideSceneConfig,
};

/**
 * Какую основную сцену открывать при выходе (back-кнопка) из inside-сцены.
 * Ключ — id inside-сцены, значение — id родителя.
 */
export const INSIDE_SCENE_PARENT: Record<string, string> = {
  'kaaba-inside': 'kaaba',
  'kaaba-inside-scene': 'kaaba',
};

export const getInsideSceneConfig = (id: string): SceneConfig | undefined =>
  insideSceneConfigs[id];

/**
 * Канонический id inside-сцены: первый ключ реестра, указывающий на тот же
 * `SceneConfig` (тот же порядок, что в `getInsideScenesForParent`). Нужен,
 * чтобы схлопывать алиасы (`kaaba-inside-scene` → `kaaba-inside`) при
 * session-трекинге посещённых inside-сцен в меню. Неизвестный id — как есть.
 */
export const getCanonicalInsideSceneId = (id: string): string => {
  const config = insideSceneConfigs[id];
  if (!config) return id;
  for (const [key, candidate] of Object.entries(insideSceneConfigs)) {
    if (candidate === config) return key;
  }
  return id;
};

export const isInsideScene = (id: string): boolean => id in insideSceneConfigs;

export const getInsideSceneIds = (): string[] => Object.keys(insideSceneConfigs);

/**
 * Канонический список inside-сцен для основной сцены (ключ — URL-id
 * родителя), с дедупом алиасов: `kaaba-inside` и `kaaba-inside-scene`
 * указывают на ОДИН и тот же `SceneConfig`, поэтому в выдаче остаётся
 * одна запись — первый встреченный id (он же используется как ключ
 * навигации и как ключ тура в `$tourByScene`).
 *
 * Используется меню (дерево внутренних сцен под карточкой основной сцены).
 */
export const getInsideScenesForParent = (
  parentUrlId: string,
): { id: string; parentUrlId: string }[] => {
  const seen = new Set<SceneConfig>();
  const result: { id: string; parentUrlId: string }[] = [];
  for (const [id, config] of Object.entries(insideSceneConfigs)) {
    if (INSIDE_SCENE_PARENT[id] !== parentUrlId) continue;
    if (seen.has(config)) continue;
    seen.add(config);
    result.push({ id, parentUrlId });
  }
  return result;
};
